import { parseMoneyToCents } from './money'
import { sizeCmFromName, type KomboCount } from './pizza-config'
import { tt } from './i18n/translation-memory'

/**
 * The Kombo BOX: three pizzas in one box, priced as one line.
 *
 * **The price is the dearest of the three, not their sum** — legacy's `Math.max(...)`, and it is the
 * offer rather than a bug: a kombo is one large pizza's worth of box with three halves in it, and
 * whichever is dearest sets what it costs.
 *
 * **The size is the kombo size, not each pizza's own.** `getPriceForKombo` looks for the pizza's price
 * at the configured kombo size, falls back to its **largest** size, and only then to its base price.
 * That chain matters and is kept exactly: a pizza sold in 32 and 40 cm has no 50 cm price, and charging
 * its 32 cm price inside a 50 cm box would sell the box at a third off.
 *
 * All money is integer cents (§14.7). Legacy uses `parseFloat` throughout, which is what the rule exists
 * to prevent.
 */

export interface KomboPizza {
  id: number
  name: string
  price: unknown
  /** The per-size price list, as the API sends it: a JSON string, or already parsed. */
  pizzaSizes?: unknown
  active?: boolean
}

export interface KomboSize {
  label: string
  price: unknown
}

/** The size list, whether it arrives as JSON text or as an array. */
export function sizesOf(pizza: KomboPizza): KomboSize[] {
  const raw = pizza.pizzaSizes
  const parsed = typeof raw === 'string' ? safeParse(raw) : raw
  if (!Array.isArray(parsed)) return []
  return parsed.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    return [{ label: typeof row.label === 'string' ? row.label : '', price: row.price }]
  })
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * What one pizza costs inside a kombo box of this size, in cents.
 *
 * Exact size match, then the largest size on offer, then the pizza's base price — legacy's order, kept.
 */
export function komboPriceCents(pizza: KomboPizza, komboCm: number): number {
  const sizes = sizesOf(pizza)
  const exact = sizes.find((size) => sizeCmFromName(size.label) === komboCm)
  if (exact !== undefined) return parseMoneyToCents(exact.price as string | number | null)
  const largest = sizes[sizes.length - 1]
  if (largest !== undefined) return parseMoneyToCents(largest.price as string | number | null)
  return parseMoneyToCents(pizza.price as string | number | null)
}

export type KomboSlots = readonly (number | null)[]

/** The box every franchise had before §9.798. Builders start from `emptyKombo(komboPizzaCount(config))`. */
export const EMPTY_KOMBO: KomboSlots = [null, null, null]

/** One empty select per pizza the box may hold - §9.798. */
export function emptyKombo(count: KomboCount): KomboSlots {
  return Array.from({ length: count.max }, () => null)
}

/** The chosen pizzas, empty selects skipped - a range box may leave the optional ones, in any position. */
export function chosenSlots(slots: KomboSlots): number[] {
  return slots.filter((slot): slot is number => slot !== null)
}

/** Whether a select past the minimum - drawn, and allowed to stay empty. */
export function isOptionalSlot(index: number, count: KomboCount): boolean {
  return index >= count.min
}

/**
 * Whether the box may be added: at least `minimum` pizzas chosen - §9.798.
 *
 * **`minimum` is required, not defaulted to *every select*.** An optional count dropped at a call site compiles
 * and quietly puts back the three-exactly rule this entry replaced (§9.480's reason for a required parameter).
 */
export function isComplete(slots: KomboSlots, minimum: number): boolean {
  return chosenSlots(slots).length >= Math.max(1, minimum)
}

/**
 * The box's price: the dearest chosen pizza, in cents.
 *
 * Zero until the minimum is chosen - a part-built box has no price, and showing a running figure that
 * only ever goes up would read as the box costing that much already.
 */
export function komboTotalCents(
  slots: KomboSlots,
  pizzas: readonly KomboPizza[],
  komboCm: number,
  minimum: number,
): number {
  if (!isComplete(slots, minimum)) return 0
  const prices = chosenSlots(slots).map((slot) => {
    const pizza = pizzas.find((candidate) => candidate.id === slot)
    return pizza === undefined ? 0 : komboPriceCents(pizza, komboCm)
  })
  // A plain integer max: these are already cents (FB-056).
  return prices.reduce((dearest, price) => (price > dearest ? price : dearest), 0)
}

/** `Margherita + Šunková + Hawai` — the line's name, and the only description the kitchen gets. */
export function komboName(slots: KomboSlots, pizzas: readonly KomboPizza[]): string {
  return slots
    .map((slot) => pizzas.find((candidate) => candidate.id === slot)?.name ?? '')
    .filter((name) => name !== '')
    .join(' + ')
}

/**
 * The one spelling of the kombo prefix - §9.430.
 *
 * A kombo line carries a negative product id and no ingredients, so the kitchen recovers its three
 * pizzas from the **name**: strip this, split on `+`, look each one up. That makes the prefix a
 * wire format rather than a label, and it was spelled three ways: legacy and the kitchen reader say
 * `Kombo Pizza:`, the customer app said `${tt('Kombo pizza')}:`, and the manager's builder stored no
 * prefix at all. Only the first was readable.
 *
 * **Capital, because 140 rows already carry it** and rewriting them is a production-data change
 * nobody needs: the reader below is widened instead.
 *
 * **And never `tt()`.** `tt` is the live translation memory, so a translated prefix stores whatever
 * language the customer was browsing in into an `order_items` row the kitchen, the receipt and the
 * till all read back. A stored name is data.
 */
export const KOMBO_NAME_PREFIX = 'Kombo Pizza'

/** `Kombo Pizza: Margherita + Šunková + Hawai` - what every writer stores, from one place. */
export function komboLineName(names: string): string {
  return `${KOMBO_NAME_PREFIX}: ${names}`
}

/** A gift line keeps its marker in front of the name, so the prefix is not at position 0. */
const GIFT_MARKER = /^\s*🎁\s*(DARČEK:\s*)?/iu

/**
 * The three pizzas of a kombo, or `null` when the name is not one - §9.430.
 *
 * Deliberately tolerant, because four shapes of this name exist in the owner's data and three of
 * them were written by something that is still running:
 *
 *   `Kombo Pizza: A + B + C`        legacy, 140 rows
 *   `Kombo pizza: A + B + C`        the customer app before this entry
 *   `🎁 DARČEK: Kombo Pizza: A + B` a gifted kombo
 *   `A + B + C`                     the manager's builder before this entry, prefix and all missing
 *
 * The last is why `composed` exists: a bare name is only a kombo if the caller already knows the
 * line is a composed one, which on the kitchen card is what a negative product id says. Guessing it
 * from the `+` alone would claim every `Coca-Cola + Fanta` in the catalogue.
 */
export function komboHalvesOf(
  productName: string | null | undefined,
  options: { composed?: boolean } = {},
): string[] | null {
  if (!productName) return null

  const withoutGift = productName.replace(GIFT_MARKER, '')
  const lowered = withoutGift.toLowerCase()
  const prefix = `${KOMBO_NAME_PREFIX.toLowerCase()}:`

  const names = lowered.startsWith(prefix)
    ? withoutGift.slice(prefix.length)
    : options.composed === true && withoutGift.includes('+')
      ? withoutGift
      : null
  if (names === null) return null

  const halves = names
    .split('+')
    .map((half) => half.trim())
    .filter((half) => half !== '')
  return halves.length > 0 ? halves : null
}

/**
 * Why the box cannot be added yet.
 *
 * An unfilled slot first, ahead of anything else — FB-190's lesson from the burger box, where the
 * config's own `required` rule was checked before the box was checked for being empty at all.
 */
export function komboProblem(
  slots: KomboSlots,
  pizzas: readonly KomboPizza[],
  minimum: number,
): string {
  if (!isComplete(slots, minimum)) {
    // §9.798 - counted down to the minimum, not to the number of selects: in a 2-4 box two chosen is enough.
    const missing = Math.max(1, minimum) - chosenSlots(slots).length
    return `${tt('Vyberte ešte pizze')}: ${missing}`
  }
  const unknown = chosenSlots(slots).filter(
    (slot) => !pizzas.some((candidate) => candidate.id === slot && candidate.active !== false),
  )
  if (unknown.length > 0) return tt('Niektorá z vybraných pizz už nie je v ponuke.')
  return ''
}

/** The pizzas a slot may hold: on the menu, and priced. */
export function selectablePizzas(pizzas: readonly KomboPizza[]): KomboPizza[] {
  return pizzas.filter((pizza) => pizza.active !== false)
}

/**
 * `komboEnabled` and `komboSizeCm` used to live here as well, taking `unknown`.
 *
 * They were a second copy of `pizza-config`'s `isKomboEnabled` and `komboSizeCm` — same rule, same
 * defaults — and the two only coexisted because this file was in `customer-menu` and could not see
 * them. Moving it into the package put both in one namespace and the compiler said so, which is the
 * useful half of a move: a duplication nobody could see becomes a duplicate identifier.
 */


/**
 * The three pizzas a kombo is assembled from — `BACKEND.md` §10A.14.
 *
 * **Their sum is roughly three times what the kombo costs, and that is correct.** A kombo is priced
 * at the *dearest* of its three at the kombo's own size (`komboTotalCents`), which is the whole
 * offer. So the server's bound — which only ever corrects a line *down* — never fires for a kombo,
 * and is not meant to: it stops a line claiming more than what it declares, not less.
 *
 * What the parts are actually for here is the id on each one. Unlike a custom pizza, whose size and
 * toppings have no catalogue row, every kombo part names a real product — so the kitchen can be told
 * what to make (§9.55) and the server can re-price a part rather than take the figure sent.
 *
 * Empty slots are dropped rather than sent as blanks; an incomplete kombo cannot be added anyway.
 */
export function komboParts(
  slots: KomboSlots,
  pizzas: readonly KomboPizza[],
  komboCm: number,
): { productId: number; name: string; unitPriceCents: number; quantity: number }[] {
  return slots.flatMap((slot) => {
    const pizza = pizzas.find((candidate) => candidate.id === slot)
    return pizza === undefined
      ? []
      : [
          {
            productId: pizza.id,
            name: pizza.name,
            unitPriceCents: komboPriceCents(pizza, komboCm),
            quantity: 1,
          },
        ]
  })
}
